import FrontEndApiService from "@services/frontend.api.service";
import {get} from "lodash";
import {SELLER} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/LuAccountTypeAppResource";
import SellerAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/SellerAppResource";
import OrderSellerAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderSellerAppResource";
import OrderAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderAppResource";
import {getSellersByField, orderPaymentTermIdFromHslpConvert} from "@helpers/order";
import {runAsPublic, setupEngineAndAxiosInterceptors} from "@helpers/api-helper";
import dayjs from "dayjs";
import OrderPropertyAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderPropertyAppResource";
import {SST} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/LuOrderSolutionAppResource";
import {
    SELLER as PURCHASED_BY_SELLER
} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/LuHslpUpsellPurchasedByTypeAppResource";
import HomeSaleLegalProtectionAppResource
    from "@sellers-shield-home-protection/ss-core-js/src/AppResources/HomeSaleLegalProtectionAppResource";
import EscrowOfficerAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/EscrowOfficerAppResource";
import {ADDRESS_CONFIRMED} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/LuOrderStatusAppResource";

export default {
    data: () => ({
        orderUuid: null,
        userUuid: null,
        invitedSeller: false,
        invitationAppResource: null,
        token: null,
        tokenFromSms: false,
        basicInfo: {
            accountType: SELLER,
        },
        referringPlatformUuid: null,
        agentUuid: null,
        solutionPurchasedId: SST,
        payment: {
            payAtCloseInputs: null,
            additionalLegalOwners: null,
            hslp: null,
        },
        orderPropertyUuid: null,
        property: {
            address: '',
            state: '',
            city: '',
            zip: '',
            stateDisabled: true,
        },
        existingHslpUuid: null,
        existingPropertyUuid: null,
        existingEscrowOfficerUuid: null,
        broker: null
    }),

    computed: {
        invitationUuid() {
            return get(this.invitationAppResource || {}, 'uuid', get(this.invitationAppResource || {}, '_uuid'));
        },
        invitationMeta() {
            return get(this.invitationAppResource || {}, 'meta', {});
        },
        invitationPrimarySeller() {
            return get(this.invitationMeta, 'primarySeller', get(this.invitationMeta, 'primary_seller', null));
        },
        invitationSecondarySeller() {
            return get(this.invitationMeta, 'secondarySeller', get(this.invitationMeta, 'secondary_seller', null));
        },
        invitationProperty() {
            return get(this.invitationMeta, 'property', null);
        },
        invitationHslp() {
            return get(this.invitationMeta, 'hslp', null);
        },
    },

    methods: {
        async registerOrder() {
            try {
                await this.createOrder();
                await this.createOrUpdateOrderProperty();
                await this.createOrderPrimarySeller(this.userUuid);
                await this.createOrderSecondarySeller();
                await this.createOrUpdateOrderHslp();
                await this.updateInvitation();
            } catch (e) {
                console.log(e);
            }
        },
        async createOrder() {
            await runAsPublic(async () => {
                if (!this.orderUuid) {
                    const orderAppResource = new OrderAppResource({});
                    orderAppResource.setAttribute('solutionPurchasedId', this.solutionPurchasedId);
                    orderAppResource.setAttribute('agentEntryToken', Math.random().toString(36).substring(7));
                    orderAppResource.setAttribute('sellerEntryToken', Math.random().toString(36).substring(7));
                    if (this.solutionPurchasedId !== SST) {
                        orderAppResource.setAttribute('orderedById', PURCHASED_BY_SELLER);
                        const {hslp} = this.payment;
                        orderAppResource.setAttribute('paymentTermId', orderPaymentTermIdFromHslpConvert(hslp.paymentTermId));
                    }
                    if (this.referringPlatformUuid) {
                        orderAppResource.setAttribute('referringPlatformUuid', this.referringPlatformUuid);
                    }
                    orderAppResource.setAttribute('agentUuid', this.agentUuid);
                    await orderAppResource.save();
                    this.orderUuid = orderAppResource.uuid;
                    this.broker = orderAppResource.broker;
                } else {
                    const orderAppResource = new OrderAppResource({
                        uuid: this.orderUuid,
                    });

                    await orderAppResource.find([
                        'property',
                        'homeSaleLegalProtection',
                        'escrowOfficer',
                        'broker'
                    ]);

                    if (orderAppResource && !!orderAppResource._uuid) {
                        orderAppResource.setAttribute('statusId', ADDRESS_CONFIRMED);
                        await orderAppResource.runUpdate();
                    }

                    //get related uuids
                    this.existingHslpUuid = orderAppResource?.homeSaleLegalProtection?.uuid;
                    this.existingPropertyUuid = orderAppResource?.property?.uuid;
                    this.existingEscrowOfficerUuid = orderAppResource?.escrowOfficer?.uuid;
                    this.broker = orderAppResource.broker;
                }
                
            });
        },
        async createOrUpdateOrderHslp() {
            setupEngineAndAxiosInterceptors();

            if (!this.orderUuid || this.invitedSeller) {
                return;
            }

            let invitationHslp = this.invitationHslp?.uuid || this.existingHslpUuid
                ? {uuid: this.invitationHslp.uuid || this.existingHslpUuid}
                : {};
            const {hslp} = this.payment;
            const hslpAppResource = new HomeSaleLegalProtectionAppResource(invitationHslp, !!invitationHslp.uuid);

            this.solutionPurchasedId = hslp.solutionPurchasedId;
            hslpAppResource.setAttribute('tier', hslp.tier);
            hslpAppResource.setAttribute('legalProtectionAmount', hslp.legalProtectionAmount);
            hslpAppResource.setAttribute('paymentTermId', hslp.paymentTermId);
            hslpAppResource.setAttribute('solutionPurchasedId', hslp.solutionPurchasedId);
            hslpAppResource.setAttribute('yearsLegalProtection', hslp.yearsLegalProtection);
            hslpAppResource.setAttribute('upsellPurchasedById', PURCHASED_BY_SELLER)
            hslpAppResource.setAttribute('upsellPaymentTermId', hslp.paymentTermId);
            hslpAppResource.setAttribute('orderUuid', this.orderUuid);
            hslpAppResource.setAttribute('paymentFromId', this.currentPageId);
            hslpAppResource.setAttribute('upsellPurchasedAt', dayjs(Date.now()));
            hslpAppResource.setAttribute('upsellPrice', hslp.price);

            if (!!hslpAppResource.uuid) {
                for (let attName in hslpAppResource.attributes) {
                    hslpAppResource._dirty[attName] = true;
                }
            }

            await hslpAppResource.save();

            if (this.payment.payAtCloseInputs) {
                let oEscrowOfficer = this.existingEscrowOfficerUuid
                    ? {uuid: this.existingEscrowOfficerUuid}
                    : {};

                const {escrowOfficerName, escrowOfficerEmail} = this.payment.payAtCloseInputs;

                const escrowOfficerAppResource = new EscrowOfficerAppResource(oEscrowOfficer, !!oEscrowOfficer?.uuid);
                escrowOfficerAppResource.setAttribute('fullName', escrowOfficerName);
                escrowOfficerAppResource.setAttribute('email', escrowOfficerEmail);
                escrowOfficerAppResource.setAttribute('orderUuid', this.orderUuid);
                await escrowOfficerAppResource.save();
            }
        },
        async createOrUpdateOrderProperty() {
            !this.orderUuid || this.orderPropertyUuid || await runAsPublic(async () => {
                let invitationProperty = this.invitationProperty?.uuid || this.existingPropertyUuid
                    ? {uuid: this.invitationProperty?.uuid || this.existingPropertyUuid}
                    : {};
                const orderPropertyAppResource = new OrderPropertyAppResource(invitationProperty, !!invitationProperty?.uuid);
                const {address, zip, city, state, formattedStreetAddress} = this.property;

                orderPropertyAppResource.setAttribute('stateId', state);
                orderPropertyAppResource.setAttribute('streetAddress', address);
                orderPropertyAppResource.setAttribute('formattedStreetAddress', formattedStreetAddress);
                orderPropertyAppResource.setAttribute('city', city);
                orderPropertyAppResource.setAttribute('zip', zip);
                orderPropertyAppResource.setAttribute('orderUuid', this.orderUuid);

                if (!!orderPropertyAppResource.uuid) {
                    for (let attName in orderPropertyAppResource.attributes) {
                        orderPropertyAppResource._dirty[attName] = true;
                    }
                }

                await orderPropertyAppResource.save();
                this.orderPropertyUuid = orderPropertyAppResource.uuid;

            });
        },
        async createOrderSellerByEmail(email, userUuid = null) {
            try {
                if (email && userUuid && this.orderUuid) {
                    email = email.toLowerCase();

                    if ((this.invitationPrimarySeller?.email || '').toLowerCase() === email) {
                        await this.createOrderPrimarySeller(userUuid, email, this.invitationPrimarySeller);
                    }
                    if ((this.invitationSecondarySeller?.email || '').toLowerCase() === email) {
                        await this.createOrderSecondarySeller(userUuid, email);
                    }
                }
            } catch (error) {
                console.log("Error", error);
            }
        },
        async createOrderPrimarySeller(userUuid, emailSeller = null, primarySeller = null) {
            if (!!primarySeller) {
                const {email, firstName, lastName, phone} = primarySeller || {};
                return await this.createOrUpdateOrderSeller(emailSeller || email, firstName, lastName, phone, 'primary', userUuid);
            }
            const {firstName, lastName, phone} = this.basicInfo;
            const {email} = this.credentials;
            return await this.createOrUpdateOrderSeller(emailSeller || email, firstName, lastName, phone, 'primary', userUuid);

        },
        async createOrderSecondarySeller(userUuid = null, emailSeller = null) {
            let iss = this.invitationSecondarySeller;
            if (emailSeller || iss?.email || iss?.firstName || iss?.lastName) {
                const {email, firstName, lastName, phone} = this.invitationSecondarySeller;
                await this.createOrUpdateOrderSeller(emailSeller || email, firstName, lastName, phone, 'secondary', userUuid);
            }
        },
        async updateOrderSellers(userUuid = null, fieldValue, fieldName = 'type') {
            let orderUuid = this.orderUuid, sellers = [];
            !(userUuid && orderUuid && fieldValue && fieldName) || await runAsPublic(async (config) => {
                let appResourceFetch = new OrderAppResource({});
                appResourceFetch.uuid = orderUuid;
                let response = await appResourceFetch.find([
                    'sellers'
                ]);
                const order = response.resources.get(orderUuid);
                sellers = getSellersByField(order, fieldValue, fieldName);
                if (sellers.length) {
                    for (const i in sellers) {
                        if (!sellers[i].userUuid) {
                            await FrontEndApiService.post(`${config.apiPrefix}/${config.apiVersion}/sellers/${sellers[i].uuid}/update`, {
                                'user_uuid': userUuid
                            });
                        }
                    }
                }
            });
            return sellers.length > 0;
        },
        async createOrUpdateOrderSeller(email, firstName, lastName, phone, type = 'primary', userUuid = null) {
            !this.orderUuid || !userUuid && !type ||
            await this.updateOrderSellers(userUuid, type) ||
            !email && !firstName && !lastName ||
            await runAsPublic(async () => {
                let sellerAppResource = new SellerAppResource({});

                sellerAppResource.setAttribute('email', (email || '').toLowerCase());
                sellerAppResource.setAttribute('firstName', firstName);
                sellerAppResource.setAttribute('lastName', lastName);
                sellerAppResource.setAttribute('phone', phone);
                !userUuid || sellerAppResource.setAttribute('userUuid', userUuid);
                sellerAppResource.setAttribute('type', type);
                await sellerAppResource.save();

                if (sellerAppResource.uuid) {
                    let orderSellerAppResource = new OrderSellerAppResource({});
                    orderSellerAppResource.setAttribute('orderUuid', this.orderUuid);
                    orderSellerAppResource.setAttribute('sellerUuid', sellerAppResource.uuid);
                    await orderSellerAppResource.save();
                }
            });
        },
        async updateInvitation() {
            if (this.invitedSeller && this.invitationUuid) {
                await runAsPublic(async (config) => {
                    await FrontEndApiService.post(`${config.apiPrefix}/${config.apiVersion}/invitations/${this.invitationUuid}`, {
                        acceptedAt: dayjs(Date.now()),
                        meta: {
                            acceptedFrom: this.tokenFromSms ? 'SMS' : (this.token ? 'Email' : 'Website'),
                            ...this.invitationMeta
                        }
                    });
                });
            }
        },
    },
};
